<template>
  <div>

    <!-- Info banner -->
    <BaseAlert icon="bi bi-info-circle-fill" variant="primary" dismissible class="mb-4">
      Billing activates automatically when a BDX is approved via BDX Match by the DCA.
      Use the controls below to manually override per TPA.
    </BaseAlert>

    <!-- Error state -->
    <b-alert v-if="error" show variant="danger" dismissible @dismissed="error = null" class="mb-3">
      {{ error }}
    </b-alert>

    <!-- Disable billing confirmation modal -->
    <b-modal
      v-model="showDisableModal"
      centered
      no-close-on-backdrop
      hide-header
      hide-footer
      size="lg"
      @hide="onDisableModalHide"
    >
      <div class="dm-root">

        <!-- Close button -->
        <button class="dm-close" @click="onDisableModalHide" aria-label="Close">
          <b-icon icon="x" />
        </button>

        <!-- Header -->
        <div class="dm-header">
          <div class="dm-header__icon-wrap">
            <b-icon icon="toggle-off" class="dm-header__icon" />
          </div>
          <div>
            <p class="dm-header__title">Disable Billing</p>
            <p class="dm-header__sub" v-if="disablePendingContext && disablePendingContext.type === 'bulk'">
              Turning off billing for
              <strong>{{ disablePendingContext.count }} TPA{{ disablePendingContext.count !== 1 ? 's' : '' }}</strong>
            </p>
            <p class="dm-header__sub" v-else-if="disablePendingContext">
              Turning off billing for
              <strong>{{ disablePendingContext.tpa.name }}</strong>
            </p>
          </div>
        </div>

        <!-- Info box -->
        <div class="dm-info">
          <p class="dm-info__label">
            <b-icon icon="info-circle-fill" class="mr-1" /> What this means
          </p>
          <div class="dm-info__row">
            <span class="dm-info__check">&#10003;</span>
            <span>Future new BDX keys will <strong>not</strong> have billing automatically enabled.</span>
          </div>
          <div class="dm-info__row">
            <span class="dm-info__check">&#10003;</span>
            <span>Existing keys will remain billable unless manually switched off at the key level.</span>
          </div>
          <div class="dm-info__row">
            <span class="dm-info__check">&#10003;</span>
            <span>Existing and pending invoice charges are <strong>not affected</strong> and will continue through the normal billing process.</span>
          </div>
        </div>

        <!-- Actions -->
        <div class="dm-actions">
          <b-button variant="secondary" @click="onDisableModalHide">Keep Enabled</b-button>
          <b-button variant="danger" @click="confirmDisable">Disable</b-button>
        </div>

      </div>
    </b-modal>

    <!-- Loading state -->
    <div v-if="loading" class="text-center py-5 text-muted small">
      Loading billing configuration...
    </div>

    <template v-if="!loading">

      <!-- Bulk action bar -->
      <div v-if="selectedIds.length > 0" class="bulk-bar mb-3 px-3 py-2 d-flex align-items-center flex-wrap">
        <span class="bulk-count mr-3">{{ selectedIds.length }} selected</span>
        <div class="d-flex align-items-center mr-3">
          <label class="bulk-label mr-2 mb-0">Start Submission Month</label>
          <input
            v-model="bulkMonth"
            type="month"
            class="month-input"
            :min="_nextMonth()"
          />
        </div>
        <b-button size="sm" variant="primary" :disabled="bulkSaving" class="mr-2" @click="bulkEnable">
          {{ bulkSaving ? 'Saving…' : 'Enable' }}
        </b-button>
        <b-button size="sm" variant="outline-danger" :disabled="bulkSaving" @click="bulkDisable">
          {{ bulkSaving ? 'Saving…' : 'Disable' }}
        </b-button>
      </div>

      <!-- Table -->
      <b-card class="mb-0" body-classes="p-0">
        <template slot="header">
          <div class="d-flex justify-content-between align-items-center flex-wrap">
            <span class="section-title font-weight-bold text-uppercase small">
              TPA BILLING
              <span class="section-count ml-2">{{ tpas.length }} TPAs</span>
            </span>
            <div class="navbar-search form-inline ml-auto">
              <b-form-group class="mb-0">
                <b-input-group class="input-group-alternative input-group-merge">
                  <div class="input-group-prepend">
                    <span class="input-group-text"><i class="fas fa-search"></i></span>
                  </div>
                  <b-form-input
                    v-model="globalSearch"
                    placeholder="Search"
                    size="sm"
                    type="text"
                    @input="currentPage = 1"
                  />
                </b-input-group>
              </b-form-group>
            </div>
          </div>
        </template>

        <div class="overflow-hidden">
          <el-table
            ref="billingTable"
            :data="paginatedTPAs"
            row-key="id"
            :empty-text="tpas.length === 0 ? 'No TPAs are linked to this underwriter.' : 'No TPAs match the selected filter.'"
            header-row-class-name="thead-light"
            :row-class-name="getTableRowClass"
            @sort-change="onSortChange"
            @selection-change="onSelectionChange"
          >
            <!-- Checkbox column (built-in el-table selection) -->
            <el-table-column type="selection" width="50" />

            <!-- TPA Name column -->
            <el-table-column
              prop="name"
              label="TPA"
              min-width="220"
              sortable="custom"
              :sort-orders="['ascending', 'descending']"
            >
              <template slot="header">
                TPA
                <b-button
                  @click.stop
                  id="billing-search-tpa"
                  class="el-table-column--search position-absolute p-0 text-primary bg-transparent p-1 border-0 shadow-none"
                >
                  <b-icon icon="search" font-scale="0.95" class="mr-0" />
                </b-button>
              </template>
              <template slot-scope="scope">
                <span :class="{ 'tpa-off': scope.row.billing_status === 0 }">{{ scope.row.name }}</span>
              </template>
            </el-table-column>

            <!-- Billing Status column -->
            <el-table-column
              prop="billing_status"
              label="Billing"
              width="160"
              sortable="custom"
              :sort-orders="['ascending', 'descending']"
            >
              <template slot="header" slot-scope="scope">
                Billing
                <b-button
                  @click.stop
                  id="billing-search-status"
                  class="el-table-column--search position-absolute p-0 text-primary bg-transparent p-1 border-0 shadow-none"
                >
                  <b-icon icon="search" font-scale="0.95" class="mr-0" />
                </b-button>
              </template>
              <template slot-scope="scope">
                <base-switch
                  :key="scope.row._switchKey"
                  :id="'billing-switch-' + scope.row.id"
                  :value="scope.row.billing_status !== 0"
                  onText=""
                  offText=""
                  :disabled="scope.row.saving"
                  @input="val => toggleBilling(scope.row, val)"
                />
              </template>
            </el-table-column>

            <!-- Start Submission Month column -->
            <el-table-column
              prop="billing_start_period"
              label="Start Submission Month"
              min-width="230"
              sortable="custom"
              :sort-orders="['ascending', 'descending']"
            >
              <template slot="header" slot-scope="scope">
                Start Submission Month
                <b-button
                  @click.stop
                  id="billing-search-month"
                  class="el-table-column--search position-absolute p-0 text-primary bg-transparent p-1 border-0 shadow-none"
                >
                  <b-icon icon="search" font-scale="0.95" class="mr-0" />
                </b-button>
              </template>
              <template slot-scope="scope">
                <input
                  v-if="scope.row.billing_status !== 0"
                  :value="scope.row.billing_start_period ? scope.row.billing_start_period.slice(0, 7) : ''"
                  type="month"
                  class="month-input"
                  :min="_nextMonth()"
                  :disabled="!isEditable(scope.row) || scope.row.saving"
                  @change="e => changeBillingDate(scope.row, e.target.value)"
                />
                <span v-else class="placeholder-date">mm/yyyy</span>
              </template>
            </el-table-column>

            <!-- Actions column -->
            <el-table-column label="Actions" width="160">
              <template slot-scope="scope">
                <div class="row-actions">
                  <template v-if="scope.row._committed_status !== 0 && !scope.row.editing">
                    <b-button size="sm" variant="outline-secondary" @click="startEdit(scope.row)">Edit</b-button>
                  </template>
                  <template v-else>
                    <b-button size="sm" variant="primary" :disabled="scope.row.saving" @click="saveEdit(scope.row)">
                      {{ scope.row.saving ? 'Saving…' : 'Save' }}
                    </b-button>
                    <b-button
                      v-if="scope.row.editing"
                      size="sm"
                      variant="link"
                      class="text-muted px-1"
                      :disabled="scope.row.saving"
                      @click="cancelEdit(scope.row)"
                    >Cancel</b-button>
                  </template>
                </div>
              </template>
            </el-table-column>
          </el-table>
        </div>

        <!-- TPA name search popover -->
        <b-popover target="billing-search-tpa" triggers="click blur" custom-class="p-2">
          <b-form @submit.prevent="applyTpaSearch">
            <b-form-input
              v-model="filterTPA"
              size="sm"
              placeholder="Search by TPA name"
              @input="currentPage = 1"
            />
            <div class="mt-2 d-flex">
              <base-button type="primary" native-type="submit" size="sm" class="ml-auto">Apply</base-button>
            </div>
          </b-form>
        </b-popover>

        <!-- Billing status search popover -->
        <b-popover target="billing-search-status" triggers="click blur" custom-class="p-2">
          <b-form @submit.prevent="currentPage = 1">
            <b-form-select
              v-model="filterBilling"
              :options="billingFilterOptions"
              size="sm"
              @change="currentPage = 1"
            />
            <div class="mt-2 d-flex">
              <base-button type="primary" native-type="submit" size="sm" class="ml-auto">Apply</base-button>
            </div>
          </b-form>
        </b-popover>

        <!-- Start Submission Month search popover — calendar picker -->
        <b-popover target="billing-search-month" triggers="click blur" custom-class="p-0">
          <b-calendar
            v-model="calendarDate"
            @selected="onMonthSelected"
          />
        </b-popover>

        <div slot="footer" class="d-flex align-items-center justify-content-center justify-content-sm-between flex-wrap">
          <div class="font-family-secondary text-sm">
            Show
            <el-select
              class="select-primary pagination-select mx-2"
              v-model="perPage"
              size="mini"
              placeholder="Per page"
              @change="currentPage = 1"
            >
              <el-option
                class="select-primary"
                v-for="item in perPageOptions"
                :key="`per-page-${item}`"
                :label="item"
                :value="item"
              />
            </el-select>
            entries
          </div>
          <BasePagination
            :total="filteredTPAs.length"
            :value="currentPage"
            :per-page="perPage"
            @change="currentPage = $event"
            class="pagination-no-border"
          />
        </div>
      </b-card>

    </template>

  </div>
</template>

<script>
import { BillingManagementService } from '@services/BillingManagementService'
import BasePagination from '@components/BasePagination'
import { BaseAlert } from '@components/base'
import { Select, Option, Table, TableColumn } from 'element-ui'

export default {
  name: 'ManageCompanyBilling',
  components: {
    BasePagination,
    BaseAlert,
    [Select.name]: Select,
    [Option.name]: Option,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
  },
  props: {
    company: { type: Object, required: true }
  },
  data() {
    const d = new Date()
    d.setDate(1)
    d.setMonth(d.getMonth() + 1)
    const nextMonth = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
    return {
      tpas: [],
      filterTPA: '',
      globalSearch: '',
      filterBilling: null,
      filterMonth: '',
      calendarDate: '',
      loading: false,
      error: null,
      selectedIds: [],
      bulkMonth: nextMonth,
      bulkSaving: false,
      showDisableModal: false,
      disablePendingContext: null,
      currentPage: 1,
      perPage: 10,
      sortField: null,
      sortOrder: null,
    }
  },
  computed: {
    perPageOptions() {
      const base = [5, 10, 25, 50]
      const total = this.tpas.length
      const result = []
      for (const n of base) {
        result.push(n)
        if (n >= total) break
      }
      return result
    },
    billingFilterOptions() {
      return [
        { value: null, text: 'All' },
        { value: 1,    text: 'Enabled' },
        { value: 0,    text: 'Disabled' },
      ]
    },
    filteredTPAs() {
      let list = this.tpas.slice()
      const gs = this.globalSearch ? this.globalSearch.toLowerCase().trim() : ''
      if (gs) {
        list = list.filter(t => {
          const status = t.billing_status !== 0 ? 'enabled' : 'disabled'
          const period = t.billing_start_period ? t.billing_start_period.slice(0, 7) : ''
          return (
            t.name.toLowerCase().includes(gs) ||
            status.includes(gs) ||
            period.includes(gs)
          )
        })
      }
      const q = this.filterTPA ? this.filterTPA.toLowerCase().trim() : ''
      if (q) {
        list = list.filter(t => t.name.toLowerCase().includes(q))
      }
      if (this.filterBilling !== null) {
        list = list.filter(t => t.billing_status === this.filterBilling)
      }
      if (this.filterMonth) {
        list = list.filter(t => t.billing_start_period && t.billing_start_period.slice(0, 7) === this.filterMonth)
      }
      if (this.sortField) {
        const asc = this.sortOrder === 'ascending'
        list = list.sort((a, b) => {
          const av = a[this.sortField] == null ? '' : a[this.sortField]
          const bv = b[this.sortField] == null ? '' : b[this.sortField]
          if (av < bv) return asc ? -1 : 1
          if (av > bv) return asc ? 1 : -1
          return 0
        })
      }
      return list
    },
    paginatedTPAs() {
      const start = (this.currentPage - 1) * this.perPage
      return this.filteredTPAs.slice(start, start + this.perPage)
    },
  },
  watch: {
    filterTPA() {
      this.currentPage = 1
    },
  },
  async created() {
    await this.loadBillingData()
  },
  methods: {
    _currentMonth() {
      return new Date().toISOString().slice(0, 7)
    },

    async loadBillingData() {
      this.loading = true
      this.error = null
      try {
        const res = await BillingManagementService.getBillingList(this.company.id)

        // LimeIQ middleware wraps all responses as {status, data}
        // res.data = {status: 200, data: [...]}  →  res.data.data = flat TPA array
        const tpaList = Array.isArray(res.data && res.data.data) ? res.data.data : []

        this.tpas = tpaList.map(tpa => {
          const billing_status = tpa.billing_status ? 1 : 0
          return {
            id: tpa.rls_tpa_id,
            name: tpa.tpa_name,
            billing_status,
            billing_start_period: (() => {
              if (tpa.billing_start_period) return tpa.billing_start_period
              if (tpa.billing_month && tpa.billing_year) {
                return `${tpa.billing_year}-${String(tpa.billing_month).padStart(2, '0')}-01`
              }
              return null
            })(),
            _committed_status: billing_status,
            editing: false,
            saving: false,
            _original: null,
            _switchKey: 0,
          }
        })
      } catch (err) {
        console.error('[BillingTab] Failed to load billing data:', err)
        this.error = 'Failed to load billing data. Please try again.'
      } finally {
        this.loading = false
      }
    },

    isEditable(tpa) {
      return tpa._committed_status === 0 || tpa.editing
    },

    isSelected(tpaId) {
      return this.selectedIds.indexOf(tpaId) !== -1
    },

    onSelectionChange(selection) {
      this.selectedIds = selection.map(function(row) { return row.id })
    },

    startEdit(tpa) {
      tpa._original = {
        billing_status: tpa.billing_status,
        billing_start_period: tpa.billing_start_period,
      }
      tpa.editing = true
    },

    cancelEdit(tpa) {
      if (tpa._original) {
        tpa.billing_status = tpa._original.billing_status
        tpa.billing_start_period = tpa._original.billing_start_period
      }
      tpa.editing = false
      tpa._original = null
    },

    async saveEdit(tpa) {
      if (tpa.billing_status !== 0 && !tpa.billing_start_period) {
        this.error = 'Please set a Billing Period before enabling billing for ' + tpa.name + '.'
        return
      }
      tpa.saving = true
      try {
        const parts = tpa.billing_start_period ? tpa.billing_start_period.slice(0, 7).split('-') : []
        await BillingManagementService.updateBilling(this.company.id, {
          tpaId: tpa.id,
          billingStatus: tpa.billing_status,
          billingMonth: parts[1] ? parseInt(parts[1]) : null,
          billingYear: parts[0] ? parseInt(parts[0]) : null,
        })
        tpa._committed_status = tpa.billing_status
        tpa.editing = false
        tpa._original = null
      } catch (err) {
        this.error = 'Failed to update billing for ' + tpa.name + '. Please try again.'
      } finally {
        tpa.saving = false
      }
    },

    _nextMonth() {
      const d = new Date()
      d.setDate(1)
      d.setMonth(d.getMonth() + 1)
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
    },

    toggleBilling(tpa, enabled) {
      if (!enabled && tpa._committed_status !== 0) {
        this.disablePendingContext = { type: 'single', tpa }
        this.showDisableModal = true
        return
      }
      tpa.editing = true
      tpa.billing_status = enabled ? 1 : 0
      tpa.billing_start_period = enabled ? this._nextMonth() + '-01' : null
    },

    changeBillingDate(tpa, monthValue) {
      tpa.billing_start_period = monthValue ? monthValue + '-01' : null
    },

    confirmDisable() {
      const ctx = this.disablePendingContext
      this.disablePendingContext = null
      this.showDisableModal = false
      if (!ctx) return
      if (ctx.type === 'single') {
        ctx.tpa.billing_status = 0
        ctx.tpa.billing_start_period = null
        this.saveEdit(ctx.tpa)
      } else {
        this._executeBulkDisable(ctx.ids)
      }
    },

    onDisableModalHide() {
      const ctx = this.disablePendingContext
      this.disablePendingContext = null
      this.showDisableModal = false
      // User cancelled — remount the switch so it snaps back to ON.
      // base-switch copies :value into local state on mount and ignores
      // subsequent prop changes, so incrementing _switchKey forces remount.
      if (ctx && ctx.type === 'single') {
        ctx.tpa._switchKey++
      }
    },

    async bulkEnable() {
      if (!this.bulkMonth) {
        this.error = 'Please select a Billing Period before enabling billing.'
        return
      }
      const offIds = this.tpas
        .filter(tpa => this.selectedIds.indexOf(tpa.id) !== -1 && tpa._committed_status === 0)
        .map(tpa => tpa.id)
      if (offIds.length === 0) return
      this.bulkSaving = true
      try {
        var updates = offIds.map(function(id) {
          return { tpa_id: id }
        })
        var bulkParts = this.bulkMonth ? this.bulkMonth.split('-') : []
        await BillingManagementService.bulkUpdateBilling(this.company.id, {
          enable: true,
          billingMonth: bulkParts[1] ? parseInt(bulkParts[1]) : null,
          billingYear: bulkParts[0] ? parseInt(bulkParts[0]) : null,
          updates: updates,
        })
        var period = this.bulkMonth + '-01'
        var month = this.bulkMonth
        this.tpas.forEach(function(tpa) {
          if (offIds.indexOf(tpa.id) !== -1) {
            tpa.billing_status = 1
            tpa.billing_start_period = period
            tpa._committed_status = 1
            tpa.editing = false
            tpa._original = null
          }
        }.bind(this))
        this.selectedIds = []
        this.$refs.billingTable && this.$refs.billingTable.clearSelection()
        this.bulkMonth = month
      } catch (err) {
        this.error = 'Bulk enable failed. Please try again.'
      } finally {
        this.bulkSaving = false
      }
    },

    bulkDisable() {
      if (this.selectedIds.length === 0) return
      const enabledIds = this.tpas
        .filter(tpa => this.selectedIds.indexOf(tpa.id) !== -1 && tpa._committed_status !== 0)
        .map(tpa => tpa.id)
      if (enabledIds.length === 0) return
      this.disablePendingContext = {
        type: 'bulk',
        count: enabledIds.length,
        ids: enabledIds,
      }
      this.showDisableModal = true
    },

    getTableRowClass({ row }) {
      if (row.saving) return 'row-saving'
      if (this.isEditable(row)) return 'row-editing'
      if (this.isSelected(row.id)) return 'row-selected'
      return ''
    },

    onSortChange({ prop, order }) {
      this.sortField = prop
      this.sortOrder = order
      this.currentPage = 1
    },

    applyTpaSearch() {
      this.currentPage = 1
    },

    onMonthSelected(date) {
      // date is YYYY-MM-DD — extract YYYY-MM for month comparison
      this.filterMonth = date ? date.slice(0, 7) : ''
      this.currentPage = 1
    },

    async _executeBulkDisable(ids) {
      this.bulkSaving = true
      try {
        var updates = ids.map(function(id) {
          return { tpa_id: id }
        })
        await BillingManagementService.bulkUpdateBilling(this.company.id, {
          enable: false,
          updates: updates,
        })
        var self = this
        this.tpas.forEach(function(tpa) {
          if (ids.indexOf(tpa.id) !== -1) {
            tpa.billing_status = 0
            tpa.billing_start_period = null
            tpa._committed_status = 0
            tpa.editing = false
            tpa._original = null
          }
        })
        self.selectedIds = self.selectedIds.filter(function(id) { return ids.indexOf(id) === -1 })
        self.$refs.billingTable && self.$refs.billingTable.clearSelection()
      } catch (err) {
        this.error = 'Bulk disable failed. Please try again.'
      } finally {
        this.bulkSaving = false
      }
    },
  }
}
</script>

<style scoped>
/* ── Section header ─────────────────────────────────── */
.section-title { color: #32325d; }
.section-count  { color: #8898aa; }

/* ── Bulk action bar ────────────────────────────────── */
.bulk-bar {
  background-color: #eaf6ff;
  border: 1px solid #b8daff;
  border-radius: 8px;
  gap: 8px;
}
.bulk-count {
  font-weight: 600;
  font-size: 0.85rem;
  color: #32325d;
  white-space: nowrap;
}
.bulk-label {
  font-size: 0.8rem;
  color: #525f7f;
  white-space: nowrap;
}

/* ── el-table row states ────────────────────────────── */
.row-saving   { opacity: 0.6; pointer-events: none; }
.row-editing  { background-color: #f0fdf8; }
.row-selected { background-color: #eaf6ff; }

.tpa-name {
  font-weight: 500;
  font-size: 0.9rem;
  color: #32325d;
}
.tpa-off { color: #adb5bd !important; }

/* ── Month input ────────────────────────────────────── */
.month-input {
  background-color: #fff;
  border: 1px solid #ced4da;
  border-radius: 6px;
  color: #495057;
  font-size: 0.8rem;
  padding: 4px 8px;
  max-width: 175px;
  width: 100%;
  outline: none;
}
.month-input:focus {
  border-color: #2dce89;
  box-shadow: 0 0 0 2px rgba(45, 206, 137, 0.2);
}
.month-input:disabled { background-color: #f8f9fa; color: #adb5bd; cursor: default; }
.placeholder-date { color: #adb5bd; font-size: 0.82rem; }

/* ── Row actions ────────────────────────────────────── */
.row-actions {
  display: flex;
  gap: 6px;
  justify-content: flex-end;
}

/* ── Disable billing modal ──────────────────────────── */
.dm-root {
  position: relative;
  padding: 4px 0 0;
}

.dm-close {
  position: absolute;
  top: -8px;
  right: -8px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background-color: transparent;
  color: #8898aa;
  font-size: 1.4rem;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;
  padding: 0;
  line-height: 1;
}
.dm-close:hover {
  background-color: #f0f0f0;
  color: #32325d;
}

.dm-header {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  margin-bottom: 20px;
}
.dm-header__icon-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background-color: #fff3cd;
  flex-shrink: 0;
}
.dm-header__icon {
  font-size: 1.5rem;
  color: #e67e22;
}
.dm-header__title {
  font-size: 1rem;
  font-weight: 700;
  color: #32325d;
  margin: 0 0 4px;
}
.dm-header__sub {
  font-size: 0.88rem;
  color: #525f7f;
  margin: 0;
}

.dm-info {
  background-color: #f8f9fa;
  border-left: 3px solid #2dce89;
  border-radius: 0 6px 6px 0;
  padding: 12px 14px;
  margin-bottom: 24px;
}
.dm-info__label {
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #8898aa;
  margin-bottom: 10px;
}
.dm-info__row {
  display: flex;
  align-items: baseline;
  gap: 8px;
  font-size: 0.88rem;
  color: #495057;
  line-height: 1.5;
  margin-bottom: 6px;
}
.dm-info__row:last-child { margin-bottom: 0; }
.dm-info__check {
  color: #2dce89;
  font-weight: 700;
  flex-shrink: 0;
  font-size: 0.9rem;
}

.dm-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}
</style>
